import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { propertyApi, unitApi, workOrderApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Loader2, Wrench, X } from 'lucide-react';
import toast from 'react-hot-toast';

interface AddMaintenanceDialogProps {
    open: boolean; // Changed prop name to match original but I'll support 'open'
    onOpenChange: (open: boolean) => void;
    preselectedPropertyId?: string;
}

type FormData = {
    propertyId: string;
    unitId: string;
    title: string;
    description: string;
    priority: string;
    category: string;
};

const CATEGORIES = [
    'Plumbing', 'Electrical', 'HVAC', 'Appliance', 'Structural', 'General', 'Other'
];

const PRIORITIES = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Emergency', label: 'Emergency' },
];

export default function AddMaintenanceDialog({ open, onOpenChange, preselectedPropertyId }: AddMaintenanceDialogProps) {
    const queryClient = useQueryClient();
    const { register, handleSubmit, watch, reset, setValue } = useForm<FormData>({
        defaultValues: {
            propertyId: preselectedPropertyId || '',
            unitId: 'none',
            priority: 'Medium',
            category: 'General'
        }
    });

    const selectedPropertyId = watch('propertyId');

    // Fetch Properties
    const { data: properties = [] } = useQuery({
        queryKey: ['properties'],
        queryFn: async () => {
            const res = await propertyApi.getAll();
            return res.data;
        }
    });

    // Fetch Units
    const { data: units = [] } = useQuery({
        queryKey: ['units', selectedPropertyId],
        queryFn: async () => {
            if (!selectedPropertyId) return [];
            const res = await unitApi.getByProperty(selectedPropertyId);
            return res.data;
        },
        enabled: !!selectedPropertyId
    });

    // Create Mutation
    const createMutation = useMutation({
        mutationFn: async (data: FormData) => {
            await workOrderApi.create({
                propertyId: data.propertyId,
                unitId: data.unitId === 'none' ? undefined : data.unitId,
                title: data.title,
                description: data.description,
                priority: data.priority,
                category: data.category
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workOrders'] });
            toast.success('Maintenance request created');
            reset();
            onOpenChange(false);
        },
        onError: (err: any) => {
            toast.error(err.response?.data?.message || 'Failed to create request');
        }
    });

    const onSubmit = (data: FormData) => {
        createMutation.mutate(data);
    };

    useEffect(() => {
        if (preselectedPropertyId) {
            setValue('propertyId', preselectedPropertyId);
        }
    }, [preselectedPropertyId, setValue]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => onOpenChange(false)}
            />

            {/* Dialog */}
            <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">

                {/* Header */}
                <div className="flex items-center justify-between border-b px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                            <Wrench className="h-5 w-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">New Request</h2>
                            <p className="text-sm text-gray-500">Create maintenance ticket</p>
                        </div>
                    </div>
                    <button
                        onClick={() => onOpenChange(false)}
                        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                    <form id="maintenance-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">Property</label>
                                <select
                                    {...register('propertyId', { required: true })}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                                >
                                    <option value="">Select...</option>
                                    {properties.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">Unit</label>
                                <select
                                    {...register('unitId')}
                                    disabled={!selectedPropertyId}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                                >
                                    <option value="none">None</option>
                                    {units.map(u => (
                                        <option key={u.id} value={u.id}>{u.unitNumber}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-700">Title</label>
                            <input
                                {...register('title', { required: true })}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                                placeholder="e.g. Leaking faucet"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-700">Description</label>
                            <textarea
                                {...register('description', { required: true })}
                                rows={4}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                                placeholder="Describe the issue..."
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">Category</label>
                                <select
                                    {...register('category')}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                                >
                                    {CATEGORIES.map(c => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">Priority</label>
                                <select
                                    {...register('priority')}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                                >
                                    {PRIORITIES.map(p => (
                                        <option key={p.value} value={p.value}>{p.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 border-t bg-gray-50 px-6 py-4 rounded-b-2xl">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" form="maintenance-form" disabled={createMutation.isPending}>
                        {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Submit
                    </Button>
                </div>

            </div>
        </div>
    );
}
